import { FC } from 'react';
import { Button } from '@new/common/ui';
import Modal from '@new/common/ui/Modal/Modal';
import { AlertTriangle } from 'lucide-react';
import { queryClient, teachurKeys, useResetQuizzesMutation } from '@teachur/http-client';
import { useSingleCourse } from '../../../../contexts/SingleCourseProvider';

interface ResetScoreModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ResetScoreModal: FC<ResetScoreModalProps> = ({ isOpen, onClose }) => {
  const { course } = useSingleCourse();
  const resetQuizMutation = useResetQuizzesMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teachurKeys.quizzes.list(course?.id || 0) });
      onClose();
    },
  });

  const handleConfirmReset = () => {
    resetQuizMutation.mutate(course?.id || 0);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col gap-8">
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <AlertTriangle className="w-6 h-6 text-(--danger-D600)" />
          </div>

          <div className="flex flex-col gap-1">
            <h2 className="[font-size:var(--fs-h6)]! lg:[font-size:var(--fs-h5)]! text-(--neutral-black-NB1000)! leading-tight font-bold m-0!">
              Reset all scores?
            </h2>

            <p className="[font-size:var(--fs-p3)]! text-(--neutral-black-NB700)! m-0!">
              This action will permanently delete all your exam attempts and score. You will need to
              start exam again from zero. Are you sure you want to continue?
            </p>
          </div>
        </div>

        <div className="flex justify-end w-full gap-2">
          <Button variant="neutral" onClick={onClose} disabled={resetQuizMutation.isPending}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleConfirmReset}
            disabled={resetQuizMutation.isPending}
            className="bg-(--danger-D700)!"
          >
            {resetQuizMutation.isPending ? 'Resetting...' : 'Reset'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
